import { motion } from "framer-motion";
import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { Analytics } from '@vercel/analytics/react'; // Vercel Analytics 임포트
// 기존 import들 아래에 추가
import { Link } from "react-router-dom"; // 라우터를 쓸 경우
/* =======================
   Constants & Copy
======================= */
const NOTIFY_LINK = "https://forms.gle/4fCqzPWm2MJjYv2N6";

const copy = {
  en: {
    notify: "Join Offline Beta",
    sections: [
      {
        title: (
          <>
            Learn Korean with <span className="hero-accent">Som-Yee</span>
          </>
        ),
        sub: "A gentle, playful 3D way to learn Hangul."
      },
      {
        title: (
          <span className="hero-title-small">
            Learn Hangul, inspired by <span className="hero-accent">Cheon-Ji-In.</span>
          </span>
        ),
        sub: `Korean vowels originate from the sky, the earth, and a person.

‘ㆍ’ represents the sky (Cheon)
‘ㅡ’ represents the earth (Ji)
‘ㅣ’ represents a person (In)

These three basic shapes combine to form every Korean vowel.
Paired with consonants, they come together to create the full Hangul alphabet.

With Som-Yee, learning Hangul feels playful and intuitive.
Build letters like stacking blocks, explore patterns as you go,
and enjoy the process of learning step by step.`
      },
      {
        title: <span className="hero-accent">Som-Yee</span>,
        sub: `Som-Yee lives on a farm with friends, each with their own personality.

Some are calm and easygoing.
Some are full of energy, always on the move.
Some even love to dance.

Together, they shape a farm that feels balanced, playful, and full of character.`
      },
      {
        title: (
          <>
            <span style={{ color: "black" }}>Sneak View of</span>
            <br />
            <span className="hero-accent">Som-Yee VR</span>
          </>
        ),
        sub: `Explore the Sneak View: VR‑style experience on your phone.
Click and drag the phone frame to move it across the background.
Enjoy discovering the scene from different angles!`
      }
    ]
  },
  kr: {
    notify: "오프라인 테스트 신청하기",
    sections: [
      {
        title: (
          <>
            <span className="hero-accent">솜이</span>와 함께 배우는 한국어
          </>
        ),
        sub: "한글을 부드럽고 즐겁게 익히는 3D 학습 경험."
      },
      {
        title: (
          <span className="hero-title-small">
            <span className="hero-accent">천·지·인</span>에서
            <br />
            시작하는 한글
          </span>
        ),
        sub: `한글의 모음은 하늘, 땅, 사람에서 출발합니다.

‘ㆍ’는 하늘(천),
‘ㅡ’는 땅(지),
‘ㅣ’는 사람(인)을 뜻해요.

이 세 가지 기본 형태가 조합되어
모든 한글 모음이 만들어집니다.
자음이 더해지며 한글이 완성되죠.

솜이와 함께라면
외우기보다 쌓고 조합하며
자연스럽게 한글을 이해하게 됩니다.`
      },
      {
        title: <span className="hero-accent">솜이</span>,
        sub: `솜이는 각기 다른 성격을 가진 친구들과
농장에서 살아가고 있어요.

차분한 친구도 있고,
에너지가 넘치는 친구도 있고,
춤추는 걸 좋아하는 친구도 있죠.

이 친구들이 모여
균형 있고 장난기 가득한 세계를 만들어갑니다.`
      },
      {
        title: (
          <>
            <span style={{ color: "black" }}>미리보기</span>
            <br />
            <span className="hero-accent">솜이 VR</span>
          </>
        ),
        sub: `솜이 모바일 VR을 미리 체험해보세요.
프레임을 클릭해 드래그하면
배경 속 공간을 자유롭게 둘러볼 수 있어요.

모바일 VR 감각의 새로운 학습 경험을 준비 중입니다.`
      }
    ]
  }
};

/* =======================
   Media Config
======================= */
const sections = [
  { src: "/preview.mp4", type: "video", frame: true, videoLeft: false, showCTA: true },
  { src: "/preview1.mp4", type: "video", frame: true, videoLeft: true },
  { src: "/preview2.gif", type: "gif", frame: false, videoLeft: false } // GIF 지원
];

/* =======================
   Media Block
======================= */
const mediaBlock = (src, x, type, frame, isLast) => {
  const commonStyle = {
    width: "60%",
    height: "auto",
    objectFit: "contain",
    margin: "auto",
    display: "block",
    background: "transparent"
  };

  return (
    <motion.div initial={{ opacity: 0, x }} animate={{ opacity: 1, x: 0 }}>
      {type === "gif" ? (
        <img src={src} alt="media" style={commonStyle} />
      ) : isLast ? (
        <video
          src={src}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          style={{ ...commonStyle, objectFit: "cover" }}
        />
      ) : frame ? (
        <div className="device-frame">
          <div className="device-screen">
            <video
              src={src}
              autoPlay
              loop
              muted
              playsInline
              preload="auto"
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        </div>
      ) : (
        <video
          className="plain-media"
          src={src}
          autoPlay
          loop
          muted
          playsInline
          preload="auto"
          style={commonStyle}
        />
      )}
    </motion.div>
  );
};

/* =======================
   PhoneFrame Component (4번째 섹션)
======================= */
const PhoneFrame = ({ title, sub }) => {
  const FRAME_WIDTH = 560;
  const FRAME_HEIGHT = 280;

  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  useEffect(() => {
    setPosition({
      x: (window.innerWidth - FRAME_WIDTH) / 2,
      y: (window.innerHeight - FRAME_HEIGHT) / 2
    });
  }, []);

  const onStart = (e) => {
    const clientX = e.type === 'touchstart' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchstart' ? e.touches[0].clientY : e.clientY;
    setOffset({ x: clientX - position.x, y: clientY - position.y });
    setDragging(true);
  };

  const onMove = (e) => {
    if (!dragging) return;

    const clientX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === 'touchmove' ? e.touches[0].clientY : e.clientY;

    const maxX = window.innerWidth - FRAME_WIDTH;
    const maxY = window.innerHeight - FRAME_HEIGHT;

    let newX = clientX - offset.x;
    let newY = clientY - offset.y;

    newX = Math.max(10, Math.min(newX, maxX));
    newY = Math.max(10, Math.min(newY, maxY));

    setPosition({ x: newX, y: newY });
  };

  const onEnd = () => setDragging(false);

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        height: "100%",
        overflow: "hidden",
        cursor: dragging ? "grabbing" : "grab",
      }}
      onMouseMove={onMove}
      onMouseUp={onEnd}
      onMouseLeave={onEnd}
      onTouchMove={onMove}
      onTouchEnd={onEnd}
      onTouchStart={onStart}
    >
      <motion.div initial={{ opacity: 0, y: 40 }} animate={{ opacity: 1, y: 0 }}>
        <h1 className="hero-title" style={{ textAlign: "center", marginBottom: "16px" }}>
          {title}
        </h1>
        <p className="hero-sub" style={{ textAlign: "center" }}>{sub}</p>
      </motion.div>

      <div
        onMouseDown={onStart}
        onTouchStart={onStart}
        style={{
          position: "absolute",
          top: `${position.y}px`,
          left: `${position.x}px`,
          width: `${FRAME_WIDTH}px`,
          height: `${FRAME_HEIGHT}px`,
          borderRadius: "48px",
          boxShadow: "0 24px 48px rgba(0,0,0,0.18)",
          backgroundColor: "#000",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "12px",
            left: "12px",
            width: `${FRAME_WIDTH - 24}px`,
            height: `${FRAME_HEIGHT - 24}px`,
            overflow: "hidden",
            borderRadius: "40px",
            backgroundColor: "#000",
          }}
        >
          <video
            src="/background.mp4"
            autoPlay
            loop
            muted
            style={{
              position: "absolute",
              width: "100vw",
              height: "100vh",
              objectFit: "cover",
              top: `-${position.y}px`,
              left: `-${position.x}px`,
            }}
          />
        </div>
      </div>

      <p
        className="hero-sub"
        style={{
          position: "absolute",
          top: `${position.y + FRAME_HEIGHT + 16}px`,
          left: `${position.x}px`,
          width: `${FRAME_WIDTH}px`,
          textAlign: "center",
          pointerEvents: "none",
        }}
      >
        Click and drag to move the frame
        <br />
        Sneak View: A mobile VR-style experience in the works
      </p>
    </div>
  );
};

/* =======================
   Main App
======================= */
export default function App() {
  const [lang, setLang] = useState("en");
  const [showPrivacy, setShowPrivacy] = useState(false);

  const togglePrivacy = (e) => {
    e.preventDefault();
    setShowPrivacy(!showPrivacy);
    window.scrollTo(0, 0);
  };

  return (
    <>
      <Helmet>
        <title>Som-Yee</title>
        <meta name="description" content="Explore Som-Yee VR: A playful mobile VR-style experience in the works" />
        <meta property="og:title" content="Som-Yee" />
        <meta property="og:description" content="Explore Som-Yee VR: A playful mobile VR-style experience in the works" />
        <meta property="og:image" content="https://yourdomain.com/thumbnail.png" />
        <meta property="og:url" content="https://yourdomain.com" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Som-Yee" />
        <meta name="twitter:description" content="Explore Som-Yee VR: A playful mobile VR-style experience in the works" />
        <meta name="twitter:image" content="https://yourdomain.com/thumbnail.png" />

        <script>
          {`(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src='https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);})(window,document,'script','dataLayer','GTM-5VX86T38');`}
        </script>
        <script async src="https://www.googletagmanager.com/gtag/js?id=G-HHRDJBR1TE"></script>
        <script>
          {`window.dataLayer = window.dataLayer || []; function gtag(){dataLayer.push(arguments);} gtag('js', new Date()); gtag('config', 'G-HHRDJBR1TE');`}
        </script>
      </Helmet>

      <Analytics />

      <style>{`
        :root { --primary: #1aa798; --bg: #f5f5f7; --text: #1d1d1f; --sub: #6e6e73; --border: #d2d2d7; }
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; background: var(--bg); color: var(--text); }
        .nav { position: fixed; top: 0; width: 100%; background: rgba(245,245,247,0.85); backdrop-filter: blur(14px); border-bottom: 1px solid var(--border); z-index: 100; }
        .nav-inner { max-width: 1040px; margin: 0 auto; padding: 14px 24px; display: flex; justify-content: space-between; align-items: center; }
        .nav-actions { display: flex; gap: 18px; align-items: center; }
        .lang-btn { background: none; border: none; font-weight: 600; cursor: pointer; }
        .nav a { color: var(--primary); font-weight: 600; text-decoration: none; font-size: 14px; }
        .hero { max-width: 1040px; margin: 0 auto; padding: 140px 24px 100px; display: grid; grid-template-columns: 1fr 1fr; gap: 60px; align-items: center; }
        @media (max-width: 900px) { .hero { grid-template-columns: 1fr; text-align: center; } }
        .hero-title { font-size: 48px; font-weight: 600; }
        .hero-title-small { font-size: 42px; font-weight: 600; }
        .hero-accent { color: var(--primary); }
        .hero-sub { margin-top: 20px; font-size: 16px; color: var(--sub); white-space: pre-line; line-height: 1.6; }
        .cta { display: inline-block; margin-top: 36px; background: var(--primary); color: #fff; padding: 14px 36px; border-radius: 999px; text-decoration: none; font-weight: 600; border: none; cursor: pointer; }
        .device-frame { width: 280px; height: 560px; background: #000; border-radius: 48px; box-shadow: 0 24px 48px rgba(0,0,0,0.18); margin: auto; position: relative; }
        .device-screen { position: absolute; inset: 12px; border-radius: 40px; overflow: hidden; }
        .device-screen video { width: 100%; height: 100%; object-fit: cover; }
        .plain-media { width: 100%; max-width: 420px; border-radius: 24px; box-shadow: 0 20px 40px rgba(0,0,0,0.12); margin: auto; }
        .footer { text-align: center; padding: 80px 24px; color: var(--sub); border-top: 1px solid var(--border); font-size: 13px; background: #fff; position: relative; z-index: 10; }
      `}</style>

      <header className="nav">
        <div className="nav-inner">
          <strong style={{ cursor: "pointer" }} onClick={() => setShowPrivacy(false)}>Som-Yee</strong>
          {!showPrivacy && (
            <div className="nav-actions">
              <button className="lang-btn" onClick={() => setLang(lang === "en" ? "kr" : "en")}>
                {lang === "en" ? "KR" : "EN"}
              </button>
              <a href={NOTIFY_LINK} target="_blank" rel="noopener noreferrer">
                {copy[lang].notify}
              </a>
            </div>
          )}
        </div>
      </header>

      {showPrivacy ? (
<motion.div
  initial={{ opacity: 0, y: 20 }}
  animate={{ opacity: 1, y: 0 }}
  style={{
    maxWidth: "800px",
    margin: "0 auto",
    padding: "140px 24px 100px",
    minHeight: "100vh"
  }}
>
  <h1 style={{ fontSize: "32px", marginBottom: "40px" }}>
    {lang === "en" ? "Privacy Policy" : "개인정보처리방침"}
  </h1>

  <div
    style={{
      background: "#fff",
      padding: "40px",
      borderRadius: "32px",
      border: "1px solid var(--border)",
      lineHeight: "1.8"
    }}
  >
    <p>
      {lang === "en"
        ? "Last updated: January 2026"
        : "최종 수정일: 2026년 1월"}
    </p>

    <br />

    <strong>1. {lang === "en" ? "Information Collection" : "정보 수집항목"}</strong>
    <p>
      {lang === "en"
        ? "The Application may collect certain information automatically, including IP address, device type, operating system, and usage data. We do not collect precise location data."
        : "애플리케이션 사용 시 IP 주소, 기기 정보, 운영 체제, 사용 기록 등의 정보가 자동으로 수집될 수 있습니다. 정확한 위치 정보는 수집하지 않습니다."}
    </p>

    <br />

    <strong>2. {lang === "en" ? "Purpose" : "이용 목적"}</strong>
    <p>
      {lang === "en"
        ? "Collected information is used to operate, maintain, and improve the Som-Yee service, and to provide important updates and notifications."
        : "수집된 정보는 솜이 서비스의 운영, 유지 및 개선과 중요한 공지 및 알림 제공을 위해 사용됩니다."}
    </p>

    <br />

    <strong>3. {lang === "en" ? "Third-Party Services" : "제3자 서비스"}</strong>
    <p>
      {lang === "en"
        ? "The Application uses third-party services that may collect information used to identify you. Please review their privacy policies below."
        : "본 애플리케이션은 개인정보를 처리하는 제3자 서비스를 이용하고 있으며, 각 서비스의 개인정보처리방침은 아래에서 확인하실 수 있습니다."}
    </p>

    <ul style={{ paddingLeft: "20px" }}>
      <li>
        <a
          href="https://www.google.com/policies/privacy/"
          target="_blank"
          rel="noopener noreferrer"
        >
          Google Play Services
        </a>
      </li>
      <li>
        <a
          href="https://unity3d.com/legal/privacy-policy"
          target="_blank"
          rel="noopener noreferrer"
        >
          Unity
        </a>
      </li>
    </ul>

    <br />

    <strong>4. {lang === "en" ? "Data Retention" : "데이터 보존 정책"}</strong>
    <p>
      {lang === "en"
        ? "User-provided data is retained only for as long as necessary to provide the service. If you wish to request data deletion, please contact us."
        : "사용자 제공 데이터는 서비스 제공에 필요한 기간 동안만 보관되며, 삭제를 원하실 경우 아래 이메일로 문의하실 수 있습니다."}
    </p>

    <br />

    <strong>5. {lang === "en" ? "Children’s Information" : "아동 개인정보"}</strong>
    <p>
      {lang === "en"
        ? "The Application is not intended for children under the age of 13, and we do not knowingly collect personal information from children."
        : "본 애플리케이션은 만 13세 미만 아동을 대상으로 하지 않으며, 아동의 개인정보를 고의로 수집하지 않습니다."}
    </p>

    <br />

    <strong>6. {lang === "en" ? "Security" : "보안"}</strong>
    <p>
      {lang === "en"
        ? "We take reasonable measures to protect your information using appropriate physical, electronic, and procedural safeguards."
        : "서비스 제공자는 물리적, 전자적, 절차적 보안 조치를 통해 개인정보를 보호하고 있습니다."}
    </p>

    <br />

    <strong>7. {lang === "en" ? "Changes to This Policy" : "개인정보처리방침의 변경"}</strong>
    <p>
      {lang === "en"
        ? "This Privacy Policy may be updated from time to time. Changes will be posted on this page."
        : "본 개인정보처리방침은 수시로 변경될 수 있으며, 변경 사항은 본 페이지를 통해 공지됩니다."}
    </p>

    <br />

    <strong>8. {lang === "en" ? "Contact Us" : "문의하기"}</strong>
    <p>
      {lang === "en"
        ? "If you have any questions about this Privacy Policy, please contact us at somyee.kr@gmail.com."
        : "개인정보 보호와 관련된 문의 사항은 somyee.kr@gmail.com으로 연락해 주세요."}
    </p>

    <br />

    <button className="cta" onClick={() => setShowPrivacy(false)}>
      {lang === "en" ? "Go Back" : "돌아가기"}
    </button>
  </div>
</motion.div>

      ) : (
        <>
          {sections.map((s, i) => {
            const text = copy[lang].sections[i];
            const isLast = i === sections.length - 1;
            return (
              <section className="hero" key={i}>
                {s.videoLeft && mediaBlock(s.src, -40, s.type, s.frame, isLast)}
                <motion.div initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }}>
                  <h1 className="hero-title">{text.title}</h1>
                  <p className="hero-sub">{text.sub}</p>
                  {s.showCTA && <a href={NOTIFY_LINK} target="_blank" rel="noopener noreferrer" className="cta">{copy[lang].notify}</a>}
                </motion.div>
                {!s.videoLeft && mediaBlock(s.src, 40, s.type, s.frame, isLast)}
              </section>
            );
          })}
          <section style={{ position: "relative", height: "1000px", marginTop: "60px" }}>
            <PhoneFrame title={copy[lang].sections[3].title} sub={copy[lang].sections[3].sub} />
          </section>
        </>
      )}

      <footer className="footer">
        <p style={{ marginBottom: "15px" }}>© 2026 Som-Yee. All rights reserved.</p>
        <a 
          href="#" 
          onClick={togglePrivacy} 
          style={{ 
            color: "var(--primary)", 
            fontSize: "14px", 
            textDecoration: "underline", 
            fontWeight: "600",
            display: "inline-block"
          }}
        >
          {lang === "en" ? "Privacy Policy" : "개인정보처리방침"}
        </a>
      </footer>

      <noscript>
        <iframe src="https://www.googletagmanager.com/ns.html?id=GTM-5VX86T38" height="0" width="0" style={{ display: "none", visibility: "hidden" }} title="Google Tag Manager"></iframe>
      </noscript>
    </>
  );
}
